// components/IdleSessionManager.jsx
import { useEffect, useRef, useState, useCallback } from "react";
import { Dialog } from "primereact/dialog";
import { Button } from "primereact/button";
import { storeTokenExpiry, getTokenExpiry, clearTokenExpiry } from "../../utils/tokenExpiry";
import './sigues-ahi.css';

/**
 * IdleSessionManager
 * - Muestra diálogo tras idleMs de inactividad ("¿sigues ahí?") [default: 10 min]
 * - Tambien muestra el dialogo cuando el token esta por vencer, aunque el
 *   usuario esté activo (basado en el 'exp' guardado por storeTokenExpiry
 *   en el login /refresh, ver utils/tokenExpiry.js)
 * - Da graceMs de gracia; si no hay respuesta => logout [default: 3 min]
 * - Si el usuario acepta => llama a refreshToken() y reinicia los temporizadores
 *
 * Props:
 *  @param refreshToken () => Promise<void>   // Llama al endpoint de refresh
 *  @param logout () => void | Promise<void>  // Cierra sesión (limpia tokens, redirige, etc.)
 *  @param idleMs? number (default 900000 = 15min)
 *  @param graceMs? number (default 180000 = 3min)
 *  @param storageKey? string (default "__lastActiveAt") para sincronizar entre pestañas
 */
export default function IdleSessionManager({
  refreshToken, logout, idleMs = 10 * 60 * 1000,  graceMs = 3 * 60 * 1000,  storageKey = "__lastActiveAt",
}) {
  const [showDialog, setShowDialog] = useState(false);
  const showDialogRef    = useRef(false);

  const [remainingMs, setRemainingMs] = useState(graceMs);

  const bcRef            = useRef(null);
  const didLogoutRef     = useRef(false);
  const idleTimeoutRef   = useRef(null);
  const graceTimeoutRef  = useRef(null);
  const expiryTimeoutRef = useRef(null);
  const tickerRef        = useRef(null);
  const deadlineRef      = useRef(null);
  const blockedRef       = useRef(false);
  const lastActiveRef    = useRef(Date.now());
  
  // Helper para abrir el dialogo y sincronizar la ref
  const openDialogState = useCallback(() => {
    showDialogRef.current = true;
    setShowDialog(true);
  }, []);

  // Helper para cerrar el dialogo y sincronizar la ref
  const closeDialogState = useCallback(() => {
    showDialogRef.current = false;
    setShowDialog(false);
  }, []);

  // Helpers de tiempo / storage
  const setLastActive = useCallback((ts) => {
    lastActiveRef.current = ts;
    try { localStorage.setItem(storageKey, String(ts)); } catch {}
  }, [storageKey]);

  const getLastActive = useCallback(() => {
    try {
      const v = Number(localStorage.getItem(storageKey));
      return isNaN(v) ? lastActiveRef.current : v;
    } catch {
      return lastActiveRef.current;
    }
  }, [storageKey]);

  // Reset del timer de inactividad
  const clearIdleTimer = useCallback(() => {
    if (idleTimeoutRef.current) clearTimeout(idleTimeoutRef.current);
    idleTimeoutRef.current = null;
  }, []);

  // Limpiar el timer de "token por vencer"
  const clearExpiryTimer = useCallback(() => {
    if (expiryTimeoutRef.current) clearTimeout(expiryTimeoutRef.current);
    expiryTimeoutRef.current = null;
  }, []);

  // Limpiar timers de gracia
  const clearGraceTimers = useCallback(() => {
    if (graceTimeoutRef.current) {
      clearTimeout(graceTimeoutRef.current);
      graceTimeoutRef.current = null;
    }
    if (tickerRef.current) {
      clearInterval(tickerRef.current);
      tickerRef.current = null;
    }
  }, []);

  // Cierra el dialogo
  const closeDialog = useCallback(() => {
    blockedRef.current = false;
    closeDialogState();
    clearGraceTimers();
  }, [closeDialogState, clearGraceTimers]);

  // Cierre de sesión automatico
  const handleAutoLogout = useCallback(async () => {
    if (didLogoutRef.current) return;
    didLogoutRef.current = true;

    // Cierra UI y timers primero para que el popup desaparezca
    closeDialog();
    clearGraceTimers();
    clearIdleTimer();
    clearExpiryTimer();
    clearTokenExpiry();

    try {
      await logout();
    } finally {
      // Señaliza a otras pestañas que se cerró sesión
      try { localStorage.setItem("__forceLogout", String(Date.now())); } catch {}
      try { bcRef.current?.postMessage({ type: 'forceLogout' }); } catch {}
    }
  }, [logout, closeDialog, clearIdleTimer, clearGraceTimers, clearExpiryTimer]);

  // Muestra el dialogo y arranca la gracia, sin importar el motivo (inactividad o vencimiento del token)
  const triggerDialog = useCallback(() => {
    // Si ya se abrio (en esta pestaña u otra), evita duplicar
    if (showDialogRef.current || blockedRef.current) return;

    blockedRef.current = true;
    clearGraceTimers();
    clearExpiryTimer(); 

    const deadline = Date.now() + graceMs;
    deadlineRef.current = deadline;

    setRemainingMs(graceMs);
    openDialogState();

    // Avisa a las otras pestañas que muestran el popup con el mismo deadline
    try { bcRef.current?.postMessage({ type: 'prompt', deadline}); } catch{}

    // Ticker de cuenta regresiva visible
    tickerRef.current = setInterval(() => {
      const rest = Math.max(0, deadlineRef.current - Date.now());
      setRemainingMs(rest);
      if (rest <= 0) {
        // seguridad por si el timeout no disparo exactamente en 0
        clearGraceTimers();
        handleAutoLogout();
      }
    }, 1000);

    // Tiempo de gracia para auto.logout
    graceTimeoutRef.current = setTimeout(async () => {
      await handleAutoLogout();
    }, graceMs);

  }, [graceMs, handleAutoLogout, clearGraceTimers, clearExpiryTimer, openDialogState]);

  // Disparado por inactividad: solo muestra el dialogo si de verdad hay rato sin actividad
  const openDialog = useCallback(() => {
    if (showDialogRef.current || blockedRef.current) return;

    const last = getLastActive();
    if (Date.now() - last < idleMs) return;

    triggerDialog();
  }, [getLastActive, idleMs, triggerDialog]);

  // Programa el aviso de "token por vencer", independiente de la inactividad.
  // Dispara con margen suficiente para que el usuario alcance a responder
  // (graceMs) y el refresh todavia se ejecute con el token vigente.
  const scheduleExpiryCheck = useCallback(() => {
    clearExpiryTimer();

    const expiresAt = getTokenExpiry(); // ms epoch
    if (!expiresAt) return;

    const EXPIRY_SAFETY_MS =  60 * 1000; // Margen extra ademas del tiempo de gracia
    const msUntilTrigger = expiresAt - Date.now() - graceMs - EXPIRY_SAFETY_MS;

    if (msUntilTrigger <= 0){
      triggerDialog();
      return;
    }

    expiryTimeoutRef.current = setTimeout(() => {
      triggerDialog();
    }, msUntilTrigger);
  }, [graceMs, clearExpiryTimer, triggerDialog]);

  const startIdleTimer = useCallback(() => {
    clearIdleTimer();
    idleTimeoutRef.current = setTimeout(() => {
      openDialog(); // Al alcanzar el límite de inactividad, mostramos diálogo e inicia la gracia
    }, idleMs);
  }, [idleMs, clearIdleTimer, openDialog]);

  // Usuario elige quedarse
  const handleStay = useCallback(async () => {
    // Usuario hizo clic en "Sí, seguir aquí"
    let data;
    try {
      clearGraceTimers();
      data = await refreshToken();
    } catch (error) {
      await handleAutoLogout(); // Caso que falle quedarse, desloguea
      return;
    }

    if(data?.exp) storeTokenExpiry(data.exp);

    // Avisa a otras pestalas ya con el refresh confirmado (y el nuevo exp ya en localstorage)
    try { bcRef.current?.postMessage({ type: 'stay' }); } catch {}

    closeDialog();
    setLastActive(Date.now());
    startIdleTimer(); // reinicia ciclo de inactividad
    scheduleExpiryCheck(); // reinicia ciclo de vencimiento de token
    
  }, [refreshToken, handleAutoLogout, closeDialog, setLastActive, startIdleTimer, clearGraceTimers, scheduleExpiryCheck]);

  // Actividad del usuario (resetea inactividad)
  const onActivity = useCallback(() => {
    const now = Date.now();

    setLastActive(now);
    startIdleTimer();

    // BroadcastChannel
    try { bcRef.current?.postMessage({ type: 'activity', ts: now }); } catch {}
    
    // Fallback por localStorage (por si el navegador no soporta BroadcastChannel)
    try { localStorage.setItem(storageKey, String(now)); } catch {}

  }, [setLastActive, startIdleTimer, storageKey]);

  // Listener para actividad en otras pestañas
  useEffect(() => {
    try {
      bcRef.current = new BroadcastChannel('idle-session');
    } catch {
      bcRef.current = null;
    }

    const onMessage = (e) => {
      const { type, ts, deadline } = e.data || {};
      
      // Llega actividad desde otra pestaña:
      if (type === 'activity') {
        // 1. actualiza marca local
        lastActiveRef.current = ts;
        try { localStorage.setItem(storageKey, String(ts)); } catch {}
        // 2. si esta pestaña está mostrando el popup, lo cierra
        if (showDialogRef.current) { closeDialog(); }
        // 3. reinicia el timer de inactividad
        startIdleTimer();
      }

      if (type === 'prompt') {
        // Abre el popup también aquí, sincronizado al mismo deadline
        clearGraceTimers();
        blockedRef.current = true;// evita duplicados
        openDialogState();
        deadlineRef.current = deadline;
        setRemainingMs(Math.max(0, deadline - Date.now()));

        // timers locales, pero con el deadline compartido
        tickerRef.current = setInterval(() => {
          const rest = Math.max(0, deadlineRef.current - Date.now());
          setRemainingMs(rest);
          if (rest <= 0) {
            clearGraceTimers();
            handleAutoLogout();
          }
        }, 1000);

        const msLeft = Math.max(0, deadline - Date.now());
        graceTimeoutRef.current = setTimeout(async () => {
          await handleAutoLogout();
        }, msLeft);
      }
      if (type === 'forceLogout') {
        handleAutoLogout();
      }
      if (type === 'stay') {
        closeDialog();
        setLastActive(Date.now());
        startIdleTimer();
        scheduleExpiryCheck();
      }
    };

    if (bcRef.current) {
      bcRef.current.onmessage = onMessage;
    }

    return () => {
      if (bcRef.current) {
        bcRef.current.onmessage = null;
        try { bcRef.current.close(); } catch {}
      }
    };

  }, [closeDialog, startIdleTimer, handleAutoLogout, storageKey, clearGraceTimers, setLastActive, openDialogState, scheduleExpiryCheck]);

  // Registra listeners de actividad 
  useEffect(() => {
    const events = ["mousemove", "mousedown", "keydown", "touchstart", "scroll", "click"];
    events.forEach((ev) => window.addEventListener(ev, onActivity, { passive: true }));
    // al montar, arranca timer y marca actividad inicial
    setLastActive(Date.now());
    startIdleTimer();
    scheduleExpiryCheck(); // programa aviso de vencimiento con eel exp guardado en el login

    return () => {
      events.forEach((ev) => window.removeEventListener(ev, onActivity));
      clearIdleTimer();
      clearExpiryTimer();
    };
  }, [onActivity, setLastActive, startIdleTimer, clearIdleTimer, scheduleExpiryCheck, clearExpiryTimer]);

  // Sincroniza entre pestañas: actividad & logout forzado
  useEffect(() => {
    const onStorage = (e) => {
      if (e.key === storageKey) {
        // Otra pestaña marcó actividad
        const otherTs = Number(e.newValue);
        if (!isNaN(otherTs) && otherTs > getLastActive()) {
          lastActiveRef.current = otherTs;
          if (!showDialogRef.current) startIdleTimer();
        }
      }
      if (e.key === "__forceLogout") {
        // Otra pestaña cerró sesión
        handleAutoLogout();
      }
    };
    window.addEventListener("storage", onStorage);
    return () => window.removeEventListener("storage", onStorage);

  }, [storageKey, getLastActive, startIdleTimer, handleAutoLogout]);

  // Pausa/ajusta cuando la pestaña se oculta/visibiliza
  useEffect(() => {
    const onVisibility = () => {
      if (document.visibilityState === "visible") {
        // al volver, sincroniza con marca más reciente (otra pestaña pudo registrar actividad)
        const last = getLastActive();
        lastActiveRef.current = last;
        
        if (!showDialogRef.current) { 
          startIdleTimer(); 
        }
        else {
          // Si el diálogo está abierto y ya venció, desloguea
          if (deadlineRef.current && Date.now() >= deadlineRef.current) { 
            handleAutoLogout(); 
          }
        }
      }
    };
    document.addEventListener("visibilitychange", onVisibility);
    return () => document.removeEventListener("visibilitychange", onVisibility);
  }, [getLastActive, startIdleTimer, handleAutoLogout]);

  // Formatea mm:ss
  const fmt = (ms) => {
    const s = Math.ceil(ms / 1000);
    const mm = String(Math.floor(s / 60)).padStart(2, "0");
    const ss = String(s % 60).padStart(2, "0");
    return `${mm}:${ss}`;
  };

  const header = (
    <div className="sa-header">
      <div className="sa-header-icon">
        <i className="pi pi-clock" />
      </div>
      <span className="sa-header-title">
        ¿Sigues ahí?
      </span>
    </div>
  );

  const footer = (
    <div className="sa-footer">
      <Button
        label="Cerrar sesión"
        icon="pi pi-sign-out"
        onClick={handleAutoLogout}
        className="sa-btn sa-btn--cancel"
      />
      <Button
        label="Sí, seguir aquí"
        icon="pi pi-check"
        onClick={handleStay}
        className="sa-btn sa-btn--confirm"
        autoFocus
      />
    </div>
  );

  return (
    <Dialog
      header={header}
      visible={showDialog}
      position="center"
      style={{ width: "28rem", maxWidth: "90vw" }}
      footer={footer}
      closable={false}
      draggable={false}
      resizable={false}
      modal
      className="sa-dialog"
    >
      <div className="sa-body-icon">
        <i className="pi pi-exclamation-triangle"/>
      </div>
      <p className="sa-description">
        No detectamos actividad por un rato. Por seguridad cerraremos tu sesión en{" "}
        <span className="sa-countdown">{fmt(remainingMs)}</span>.
      </p>
    </Dialog>
  );
}